import type { PrepareRequest, WorkerResponse } from './imageWorkerProtocol';
import { drawBitmapToScaledCanvas, type PreparedImage } from './imagePipeline';

/**
 * 图像处理客户端：Worker 优先、主线程降级。
 *
 * 降级触发路径（每条都 console.warn 可观测，绝不静默）：
 *  1. 环境无 Worker（旧浏览器）
 *  2. 构造 Worker 抛错（CSP / 加载失败）
 *  3. Worker onerror（脚本运行崩溃）
 *  4. Worker 内部不支持 OffscreenCanvas（回 error）
 *  5. 单任务超时（默认 15s）
 *  6. 上述任一发生后：从**原始 source**重建 ImageBitmap 走主线程（不因 transfer 失败丢图）
 */

const TIMEOUT_MS = 15000;

let worker: Worker | null = null;
/** null=未探测；true=可用；false=已禁用（降级模式）。 */
let workerUsable: boolean | null = null;
let nextId = 1;
const pending = new Map<
  number,
  { resolve: (r: PreparedImage) => void; reject: (e: Error) => void }
>();

function getWorker(): Worker | null {
  if (workerUsable === false) return null;
  if (worker) return worker;
  if (typeof Worker === 'undefined') {
    workerUsable = false;
    return null;
  }
  try {
    const w = new Worker(new URL('./imageWorker.ts', import.meta.url), { type: 'module' });
    w.onmessage = (e: MessageEvent<WorkerResponse>) => {
      const msg = e.data;
      const p = pending.get(msg.id);
      if (!p) return;
      pending.delete(msg.id);
      if (msg.kind === 'ready') {
        p.resolve({ canvas: msg.canvas, width: msg.width, height: msg.height });
      } else {
        p.reject(new Error(msg.message));
      }
    };
    w.onerror = (ev) => {
      console.warn('[imageWorker] 运行失败，切换主线程降级模式：', (ev as ErrorEvent).message ?? ev);
      workerUsable = false;
      const err = new Error('image worker unavailable');
      for (const [, p] of pending) p.reject(err);
      pending.clear();
      worker = null;
    };
    worker = w;
    workerUsable = true;
    return w;
  } catch (e) {
    console.warn('[imageWorker] 构造失败，切换主线程降级模式：', e);
    workerUsable = false;
    return null;
  }
}

function viaWorker(bitmap: ImageBitmap, maxDim: number): Promise<PreparedImage> {
  const w = getWorker();
  if (!w) return Promise.reject(new Error('image worker unavailable'));
  const id = nextId++;
  return new Promise<PreparedImage>((resolve, reject) => {
    const timer = setTimeout(() => {
      pending.delete(id);
      reject(new Error('image worker timeout'));
    }, TIMEOUT_MS);
    pending.set(id, {
      resolve: (r) => {
        clearTimeout(timer);
        resolve(r);
      },
      reject: (e) => {
        clearTimeout(timer);
        reject(e);
      },
    });
    const req: PrepareRequest = { kind: 'prepare', id, bitmap, maxDim };
    // ImageBitmap 是 Transferable：转移给 worker，主线程该实例失效。
    w.postMessage(req, [bitmap]);
  });
}

/**
 * 等比缩放图像。Worker 优先；任何失败都降级到主线程（从原始 source 重建）。
 * @param source HTMLImageElement 或 File（必须可重复读取，用于降级重建）
 */
export async function prepareImage(
  source: HTMLImageElement | File,
  maxDim: number,
): Promise<PreparedImage> {
  const bitmap = await createImageBitmap(source);
  try {
    return await viaWorker(bitmap, maxDim);
  } catch (err) {
    console.warn(
      '[imageWorker] 降级到主线程处理：',
      err instanceof Error ? err.message : err,
    );
    // bitmap 可能已被 worker 消耗（detached）；从原始 source 重建一份。
    const fresh = await createImageBitmap(source);
    return drawBitmapToScaledCanvas(fresh, maxDim);
  }
}
